import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import {
  ClipboardList,
  CheckCircle2,
  AlertCircle,
  Loader2,
  IndianRupee
} from 'lucide-react';
import styles from './AddManufacturerClearance.module.css'; // Reusing styles
import Footer from '../../components/Common/Footer/Footer';
import NotFound from '../Error/NotFound';


const EditManufacturerClearance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Data Options
  const [projectOptions, setProjectOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [poOptions, setPoOptions] = useState([]);
  const [bankOptions, setBankOptions] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    date: '',
    note: '',
    project_id: '',
    vendor_id: '',
    purchase_order_id: '',
    advance_date: '',
    is_advance: false,
    amount: '',
    payment_mode: '',
    bank_account_id: '',
    cheque_date: '',
    ref_transaction_number: '',
    remarks: ''
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    fetchData();
    return () => window.removeEventListener('resize', handleResize);
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsRes, vendorsRes, posRes, banksRes, clearanceRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/vendors-dropdown'),
        api.get('/purchase-orders'),
        api.get('/bank-accounts-dropdown'),
        api.get(`/manufacturer-clearances/${id}`)
      ]);

      if (projectsRes.data.success) {
        setProjectOptions(projectsRes.data.data.map(p => ({ value: p.id, label: p.project_name })));
      }
      if (vendorsRes.data.success) {
        setVendorOptions(vendorsRes.data.data.map(v => ({ value: v.id, label: v.vendor_name })));
      }
      if (posRes.data.success) {
        const poList = posRes.data.data?.data || [];
        setPoOptions(poList.map(po => ({ value: po.id, label: po.po_number })));
      }
      if (banksRes.data.success) {
        setBankOptions(banksRes.data.data.map(b => ({ value: b.id, label: `${b.bank_name} - ${b.bank_account_number}` })));
      }

      if (clearanceRes.data.status === 'success') {
        const data = clearanceRes.data.data;
        const payment = data.vendor_payments && data.vendor_payments.length > 0 ? data.vendor_payments[0] : {};

        setFormData({
          date: data.date ? new Date(data.date).toISOString().split('T')[0] : '',
          note: data.note || '',
          project_id: data.project_id,
          vendor_id: data.vendor_id,
          purchase_order_id: data.purchase_order_id,
          advance_date: data.advance_date ? new Date(data.advance_date).toISOString().split('T')[0] : '',
          is_advance: !!data.is_advance,
          amount: data.amount,
          payment_date: payment.payment_date || '',
          payment_mode: payment.payment_mode || '',
          bank_account_id: payment.bank_account_id || '',
          cheque_date: payment.cheque_date || '',
          ref_transaction_number: payment.ref_transaction_number || '',
          remarks: payment.remarks || ''
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load clearance data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    if (!formData.project_id || !formData.vendor_id || !formData.purchase_order_id || !formData.amount || !formData.date) {
      toast.error('Please fill in all required fields');
      setSubmitLoading(false);
      return;
    }

    try {
      const payload = {
        date: formData.date,
        note: formData.note,
        project_id: formData.project_id,
        vendor_id: formData.vendor_id,
        purchase_order_id: formData.purchase_order_id,
        is_advance: formData.is_advance,
        advance_date: formData.is_advance ? formData.advance_date : null,
        amount: formData.amount || 0,
        payment_mode: formData.payment_mode,
        bank_account_id: formData.bank_account_id || null,
        cheque_date: formData.cheque_date || null,
        ref_transaction_number: formData.ref_transaction_number || null,
        remarks: formData.remarks || formData.note
      };

      const response = await api.put(`/manufacturer-clearances/${id}`, payload);
      if (response.data.status === 'success') {
        toast.success('Manufacturer Clearance updated successfully');
        navigate('/manufacturer-clearance');
      }
    } catch (error) {
      console.error('Error updating clearance:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update clearance record';
      toast.error(errorMsg);
      if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0][0];
        toast.error(firstError);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  if (loading) {
    return (
      <div className={styles['clearance-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={toggleSidebar} />
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Loader2 className={styles['spinner']} size={48} />
          </div>
        </div>
      </div>
    );
  }

  if (!formData.amount) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['clearance-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''} ${isMobile ? styles['mobile-view'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <Breadcrumbs />
              <h1>Edit Manufacturer Clearance</h1>
              <p>Update manufacturer clearance details and associated payment records.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={styles['form-sections-grid']}>
              <div className={styles['form-card']}>
                <div className={styles['card-header-po']}>
                  <ClipboardList size={18} className={styles['text-orange']} />
                  <h3>Basic Information</h3>
                </div>
                <div className={styles['card-content']}>
                  <div className={styles['form-grid-2']}>
                    <div className={styles['form-group']}>
                      <label>Date <span className={styles['required-star']}>*</span></label>
                      <CustomDatePicker
                        selected={formData.date}
                        onChange={(date) => setFormData(prev => ({ ...prev, date: date.toISOString().split('T')[0] }))}
                      />
                    </div>

                    <div className={styles['form-group']}>
                      <label>Project Name <span className={styles['required-star']}>*</span></label>
                      <CustomSelect
                        options={projectOptions}
                        value={projectOptions.find(opt => opt.value === formData.project_id)}
                        onChange={(opt) => setFormData(prev => ({ ...prev, project_id: opt.value }))}
                        placeholder="Select Project"
                        isSearchable={true}
                      />
                    </div>

                    <div className={styles['form-group']}>
                      <label>Vendor Name <span className={styles['required-star']}>*</span></label>
                      <CustomSelect
                        options={vendorOptions}
                        value={vendorOptions.find(opt => opt.value === formData.vendor_id)}
                        onChange={(opt) => setFormData(prev => ({ ...prev, vendor_id: opt.value }))}
                        placeholder="Select Vendor"
                        isSearchable={true}
                      />
                    </div>

                    <div className={styles['form-group']}>
                      <label>Purchase Order <span className={styles['required-star']}>*</span></label>
                      <CustomSelect
                        options={poOptions}
                        value={poOptions.find(opt => opt.value === formData.purchase_order_id)}
                        onChange={(opt) => setFormData(prev => ({ ...prev, purchase_order_id: opt.value }))}
                        placeholder="Select PO"
                        isSearchable={true}
                      />
                    </div>

                  </div>

                  <div className={`${styles['form-group']} ${styles['full-width']}`}>
                    <label>Note</label>
                    <textarea
                      name="note"
                      placeholder="Enter additional notes here..."
                      value={formData.note}
                      onChange={handleInputChange}
                      rows="4"
                      className={styles['form-textarea']}
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className={styles['form-card']}>
                <div className={styles['card-header-po']}>
                  <IndianRupee size={18} className={styles['text-orange']} />
                  <h3>Payment Details</h3>
                </div>
                <div className={styles['card-content']}>
                  {/* is_advance Checkbox */}
                  <div className={styles['checkbox-container']}>
                    <label className={styles['checkbox-label']}>
                      <input
                        type="checkbox"
                        name="is_advance"
                        checked={formData.is_advance}
                        onChange={handleInputChange}
                      />
                      <div className={styles['checkbox-custom']}></div>
                      <span className={styles['label-text']}>Record as Advance Payment</span>
                    </label>
                  </div>

                  {formData.is_advance ? (
                    <div className={styles['advance-fields-grid']}>
                      {/* Advance Date */}
                      <div className={styles['form-group']}>
                        <label>Advance Date</label>
                        <CustomDatePicker
                          selected={formData.advance_date}
                          onChange={(date) => setFormData(prev => ({ ...prev, advance_date: date.toISOString().split('T')[0] }))}
                        />
                      </div>
                      {/* Payment Mode */}
                      <div className={styles['form-group']}>
                        <label>Payment Mode</label>
                        <CustomSelect
                          options={[
                            { value: 1, label: 'Cash' },
                            { value: 2, label: 'Bank Transfer' },
                            { value: 3, label: 'Cheque' },
                            { value: 4, label: 'UPI' }
                          ]}
                          value={[
                            { value: 1, label: 'Cash' },
                            { value: 2, label: 'Bank Transfer' },
                            { value: 3, label: 'Cheque' },
                            { value: 4, label: 'UPI' }
                          ].find(opt => opt.value === Number(formData.payment_mode))}
                          onChange={(opt) => setFormData(prev => ({ ...prev, payment_mode: opt.value }))}
                          placeholder="Select Mode"
                        />
                      </div>

                      {/* Bank Account */}
                      {[2, 3, 4].includes(Number(formData.payment_mode)) && (
                        <div className={styles['form-group']}>
                          <label>Bank Account</label>
                          <CustomSelect
                            options={bankOptions}
                            value={bankOptions.find(opt => opt.value === formData.bank_account_id)}
                            onChange={(opt) => setFormData(prev => ({ ...prev, bank_account_id: opt.value }))}
                            placeholder="Select Bank Account"
                          />
                        </div>
                      )}

                      {/* Ref Transaction Number */}
                      {[2, 3, 4].includes(Number(formData.payment_mode)) && (
                        <div className={styles['form-group']}>
                          <label>Ref / Transaction#</label>
                          <input
                            type="text"
                            name="ref_transaction_number"
                            placeholder="Enter Transaction ID / Ref No"
                            value={formData.ref_transaction_number}
                            onChange={handleInputChange}
                          />
                        </div>
                      )}

                      {/* Amount */}
                      <div className={styles['form-group']}>
                        <label>Amount</label>
                        <div className={styles['input-with-icon']}>
                          <IndianRupee size={16} />
                          <input
                            type="number"
                            name="amount"
                            placeholder="Enter Amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            step="0.01"
                          />
                        </div>
                      </div>

                      {/* Remarks */}
                      <div className={`${styles['form-group']} ${styles['full-width']}`} style={{ marginTop: '16px' }}>
                        <label>Payment Remarks</label>
                        <textarea
                          name="remarks"
                          placeholder="Add any specific payment remarks..."
                          value={formData.remarks}
                          onChange={handleInputChange}
                          rows="2"
                          className={styles['form-textarea']}
                        ></textarea>
                      </div>
                    </div>
                  ) : (
                    <div className={styles['advance-placeholder']}>
                      <div className={styles['placeholder-icon']}>
                        <AlertCircle size={32} />
                      </div>
                      <p>Check the box above to record an advance payment with this clearance.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles['header-actions-btns']}>
              <button type="button" className={styles['btn-cancel']} onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className={styles['btn-primary']} disabled={submitLoading}>
                {submitLoading ? (
                  <Loader2 size={18} className={styles['spinner']} />
                ) : (
                  <CheckCircle2 size={18} />
                )}
                <span>{submitLoading ? 'Updating...' : 'Update Clearance Record'}</span>
              </button>
            </div>
          </form>
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default EditManufacturerClearance;
